import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const EarlyDetection = () => {
  const benefits = [
    {
      icon: 'ri-heart-add-line',
      title: '予後の改善',
      desc: '早期に治療を開始することで、心機能の低下を防ぎ、長期的な予後を大きく改善できます。',
    },
    {
      icon: 'ri-hospital-line',
      title: '入院の減少',
      desc: '適切な管理により急性増悪を予防し、緊急入院のリスクを大幅に減少させることができます。',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'QOLの向上',
      desc: '症状をコントロールすることで、日常生活の質を維持し、活動的な生活を送ることができます。',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: '医療費の削減',
      desc: '入院や救急受診を減らすことで、長期的な医療費を抑制することができます。',
    },
  ];

  const timeline = [
    {
      phase: '発見前',
      title: '無症状期',
      desc: '心機能は低下しているが、自覚症状はまだ現れていない段階',
      color: 'bg-gray-200',
    },
    {
      phase: '早期発見',
      title: 'スクリーニング',
      desc: '定期検診や血液検査で心不全の兆候を発見',
      color: 'bg-yellow-200',
    },
    {
      phase: '診断',
      title: '精密検査',
      desc: '心エコーやBNP測定などで詳細な診断を実施',
      color: 'bg-orange-200',
    },
    {
      phase: '治療開始',
      title: '早期介入',
      desc: '薬物療法や生活指導を開始し、進行を抑制',
      color: 'bg-primary-200',
    },
    {
      phase: '管理',
      title: '継続フォロー',
      desc: '定期的なモニタリングで状態を維持',
      color: 'bg-green-200',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24">
        <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
              早期発見のメリット
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl">
              心不全を早期に発見することで得られる、様々なメリットをご紹介します
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">早期発見がもたらす4つのメリット</h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-base">
                心不全の早期発見は、患者さんの人生に大きな影響を与えます。適切なタイミングでの介入により、以下のような効果が期待できます。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-primary-50 border-2 border-primary-200 p-10 rounded-3xl hover:shadow-2xl transition-all group cursor-pointer"
                >
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 flex items-center justify-center bg-primary-500 rounded-2xl flex-shrink-0 group-hover:scale-110 transition-transform">
                      <i className={`${benefit.icon} text-3xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-700 leading-relaxed text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-medical-beige p-12 rounded-3xl">
              <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">早期発見から治療までの流れ</h2>
              <div className="relative">
                <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-300 transform -translate-y-1/2 hidden lg:block"></div>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 relative">
                  {timeline.map((item, index) => (
                    <div key={index} className="relative">
                      <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer">
                        <div className={`w-12 h-12 ${item.color} rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-gray-800 text-sm`}>
                          {index + 1}
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500 mb-2 font-semibold uppercase">{item.phase}</div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                          <p className="text-gray-600 text-xs leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="w-full h-96 rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/1f397ad2f987e61a579434968183cfbe.jpg"
                  alt="Early Detection Benefits"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">早期発見と遅延発見の違い</h2>
                <div className="space-y-6">
                  <div className="border-l-4 border-green-500 pl-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">早期発見の場合</h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-green-500 mt-1"></i>
                        <span>軽度の症状で治療開始が可能</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-green-500 mt-1"></i>
                        <span>心機能の低下を最小限に抑制</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-green-500 mt-1"></i>
                        <span>通常の生活を維持しやすい</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-green-500 mt-1"></i>
                        <span>入院リスクが大幅に低減</span>
                      </li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-red-500 pl-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">発見が遅れた場合</h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-close-line text-red-500 mt-1"></i>
                        <span>重度の症状で緊急入院が必要</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-close-line text-red-500 mt-1"></i>
                        <span>心機能の回復が困難</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-close-line text-red-500 mt-1"></i>
                        <span>生活の質が大きく低下</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-close-line text-red-500 mt-1"></i>
                        <span>繰り返す入院で医療費増大</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-primary-500 to-primary-600 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">定期的な検診をお勧めします</h2>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              特に高血圧、糖尿病、心臓病の既往がある方は、定期的な心不全スクリーニングを受けることをお勧めします。早期発見が、あなたの未来を守ります。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/examination"
                className="px-8 py-4 bg-white text-primary-600 text-base font-semibold rounded-full hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                検査方法を見る
              </a>
              <a
                href="/contact"
                className="px-8 py-4 bg-transparent border-2 border-white text-white text-base font-semibold rounded-full hover:bg-white hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                お問い合わせ
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EarlyDetection;
